import { Checkbox, CircularProgress, Zoom } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import React, { FunctionComponent, useEffect, useMemo, useRef } from 'react';
import { useMultiSelectWithProvider } from 'use-multiselect';
import { colors } from '../styles/theme';
import { useAssetsTableState } from '../contexts/assetsTableStateContext';
import { useFilter } from '../contexts/FilterContext/filterContext';
import { useAggregates } from '../contexts/AggregatesContext';
import { MAX_ASSET_LIMIT_MULTISELECT } from '../contexts/AggregatesContext/queryUtils';
import { useRecordType } from '../contexts/recordTypeContext';
import { MapAssetType } from '../contexts/AggregatesContext/types';
import { isEqual } from 'lodash';
import { useInfoContext } from '../contexts/InfoContext/infoContext';
import { useRecoilValue } from 'recoil';
import {
  drawFilterSelector,
  mapFilterSelector,
  quickSearchState,
  selectedRecordTypeFilterSelector,
  selectedRecordTypeState,
} from '../recoil/atoms';
import { MapFilter } from '../contexts/FilterContext/types';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    checkboxContainer: {
      height: 39,
      width: 50,
      zIndex: 100,
      color: colors.white,
      backgroundColor: colors.lightGray,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    },
    checkbox: {
      padding: 0,
    },
    indeterminate: {
      color: theme.palette.primary.main,
    },
  })
);

export interface SelectAllCheckboxProps {
  disabled?: boolean;
  forceSelect?: boolean;
  loading?: boolean;
}

/**
 * SelectAllCheckbox -
 * Uses the selectedCount and total count to determine the current selection state.
 * Shows as checked if all items are selected, unchecked if no items are selected and indeterminate if some are selected
 *
 * On click, either selects all or unselects all.
 *
 * @param props
 */
const SelectAllCheckbox: FunctionComponent<SelectAllCheckboxProps> = props => {
  const { disabled, forceSelect, loading } = props;
  const classes = useStyles();
  const { isMultiSelectActive, selectAll, deSelectAll, getSelectionState, stateHash } = useMultiSelectWithProvider();
  const {
    setIsSelectAll,
    setLimitAlert,
    setActionsMenuRefresh,
    isSelectAll,
    actionsMenuRefresh,
    setDataLoading,
    dataLoading,
  } = useAssetsTableState();
  const { selectedRecordTypeDefinition } = useRecordType();
  const selectedRecordType = useRecoilValue(selectedRecordTypeState);
  const { multiSelectSelectedAssets } = useFilter();
  const drawFilter = useRecoilValue(drawFilterSelector);
  const mapFilter = useRecoilValue(mapFilterSelector);
  const filterState = useRecoilValue(selectedRecordTypeFilterSelector);
  const quickSearch = useRecoilValue(quickSearchState);
  const { getAssetsForMultiSelect, filteredCount } = useAggregates();
  const { openSnackbar } = useInfoContext();

  const refresh = () => {
    setActionsMenuRefresh(prev => prev + 1);
  };
  const fetchAssets = async () => {
    setDataLoading(true);
    const data = await getAssetsForMultiSelect();
    if (data.length >= MAX_ASSET_LIMIT_MULTISELECT) {
      setLimitAlert(true);
      setIsSelectAll(false);
      multiSelectSelectedAssets.current = [];
      deSelectAll();
      refresh();
      setDataLoading(false);
      return;
    }
    setDataLoading(false);
    multiSelectSelectedAssets.current = data;
    selectAll();
    refresh();
  };

  // deselect automatically whenever multiselect is turned off
  useEffect(() => {
    if (!isMultiSelectActive) {
      deSelectAll();
      setIsSelectAll(false);
      multiSelectSelectedAssets.current = [];
    }
  }, [isMultiSelectActive, deSelectAll]);

  useEffect(() => {
    if (forceSelect && !!!loading) {
      selectAll();
      fetchAssets();
      setIsSelectAll(true);
    }
  }, [forceSelect, loading]);

  const handleSelectAll = () => {
    setIsSelectAll(true);
    fetchAssets();
  };

  const isIndeterminate = React.useMemo(() => {
    const selectedAssetlength = multiSelectSelectedAssets.current.length;
    if (selectedAssetlength !== 0) {
      if (isSelectAll) {
        return false;
      }
      return true;
    }
    return false;
  }, [actionsMenuRefresh, isSelectAll, stateHash]);

  useEffect(() => {
    if (multiSelectSelectedAssets.current.length) {
      const recordTypeInSelectedArray = (multiSelectSelectedAssets.current[0] as MapAssetType).recordTypeKey;
      if (selectedRecordTypeDefinition.queryKey !== recordTypeInSelectedArray) {
        multiSelectSelectedAssets.current = [];
        setIsSelectAll(false);
        deSelectAll();
      }
    }
  }, [selectedRecordTypeDefinition]);

  // maintain collection of states which affect the dataset
  const datasetConstraints = useMemo(
    () => ({
      filterState,
      drawFilter: drawFilter.enabled ? drawFilter : { enabled: drawFilter.enabled },
      mapFilterState: mapFilter.extentFilterEnabled
        ? mapFilter
        : ({ extentFilterEnabled: mapFilter.extentFilterEnabled } as MapFilter),
      quickSearch,
      selectedRecordType,
      filteredCount: filteredCount[selectedRecordType],
    }),
    [filterState, drawFilter, mapFilter, quickSearch, selectedRecordType, filteredCount]
  );

  const datasetConstraintsRef = useRef(datasetConstraints);

  // clear selections whenever dataset constraints change while `Select All` is active
  useEffect(() => {
    const datasetConstraintsChanged = !isEqual(datasetConstraintsRef.current, datasetConstraints);
    datasetConstraintsRef.current = datasetConstraints;
    const { allSelected } = getSelectionState();
    if (allSelected && datasetConstraintsChanged) {
      multiSelectSelectedAssets.current = [];
      setIsSelectAll(false);
      deSelectAll();
      openSnackbar({ title: 'Selected records cleared due to change in filters.', type: 'WARN' });
    }
  }, [datasetConstraints]);

  return (
    <Zoom in={isMultiSelectActive}>
      <div className={classes.checkboxContainer}>
        {!dataLoading ? (
          <Checkbox
            disabled={disabled}
            classes={{ root: classes.checkbox, indeterminate: classes.indeterminate }}
            indeterminate={isIndeterminate}
            checked={isSelectAll}
            color="primary"
            onClick={() => {
              if (multiSelectSelectedAssets.current.length) {
                deSelectAll();
                setIsSelectAll(false);
                refresh();
                multiSelectSelectedAssets.current = [];
              } else {
                handleSelectAll();
              }
            }}
          />
        ) : (
          <CircularProgress color="primary" size={20} />
        )}
      </div>
    </Zoom>
  );
};
export default SelectAllCheckbox;
